import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RenderingQueue = ({ renderingJobs, onCancelJob, onRetryJob }) => {
  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <Icon name="Play" size={16} className="text-blue-600" />;
      case 'pending':
        return <Icon name="Clock" size={16} className="text-yellow-600" />;
      case 'completed':
        return <Icon name="CheckCircle" size={16} className="text-green-600" />;
      case 'failed':
        return <Icon name="XCircle" size={16} className="text-red-600" />;
      default:
        return <Icon name="Circle" size={16} className="text-muted-foreground" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'text-blue-600';
      case 'pending':
        return 'text-yellow-600';
      case 'completed':
        return 'text-green-600';
      case 'failed':
        return 'text-red-600';
      default:
        return 'text-muted-foreground';
    }
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) {
      return `${minutes}m`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  const formatTimeRemaining = (seconds) => {
    if (seconds < 60) {
      return `${seconds}s`;
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes < 60) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  return (
    <div className="bg-card border border-border rounded-lg">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground">Rendering Queue</h3>
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Icon name="Activity" size={16} />
            <span>{renderingJobs?.filter(job => job?.status === 'active')?.length} active</span>
          </div>
        </div>
      </div>
      <div className="divide-y divide-border max-h-96 overflow-y-auto">
        {renderingJobs?.length === 0 ? (
          <div className="p-8 text-center">
            <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-3">
              <Icon name="Video" size={24} className="text-muted-foreground" />
            </div>
            <p className="text-muted-foreground">No rendering jobs in queue</p>
          </div>
        ) : (
          renderingJobs?.map((job) => (
            <div key={job?.id} className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3">
                  {getStatusIcon(job?.status)}
                  <div>
                    <div className="font-medium text-foreground">{job?.projectName}</div>
                    <div className="text-sm text-muted-foreground">{job?.format} • {job?.resolution}</div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`text-sm font-medium capitalize ${getStatusColor(job?.status)}`}>
                    {job?.status}
                  </span>
                  {job?.status === 'failed' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="RotateCcw"
                      onClick={() => onRetryJob(job?.id)}
                    >
                      Retry
                    </Button>
                  )}
                  {(job?.status === 'active' || job?.status === 'pending') && (
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="X"
                      onClick={() => onCancelJob(job?.id)}
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </div>

              {/* Progress Bar for Active Jobs */}
              {job?.status === 'active' && (
                <div className="mb-3">
                  <div className="flex items-center justify-between text-sm text-muted-foreground mb-1">
                    <span>Progress: {job?.progress}%</span>
                    <span>ETA: {formatTimeRemaining(job?.estimatedTimeRemaining)}</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div 
                      className="bg-primary h-2 rounded-full transition-all duration-300"
                      style={{ width: `${job?.progress}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Job Details */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Started:</span>
                  <div className="font-medium">
                    {new Date(job.startedAt)?.toLocaleTimeString('en-US', { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </div>
                </div>
                <div>
                  <span className="text-muted-foreground">Duration:</span>
                  <div className="font-medium">{formatDuration(job?.duration)}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">File Size:</span>
                  <div className="font-medium">{job?.fileSize}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">Priority:</span>
                  <div className="font-medium capitalize">{job?.priority}</div>
                </div>
              </div>

              {/* Error Message for Failed Jobs */}
              {job?.status === 'failed' && job?.errorMessage && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start space-x-2">
                    <Icon name="AlertCircle" size={16} className="text-red-600 mt-0.5" />
                    <div>
                      <div className="text-sm font-medium text-red-800">Rendering Failed</div>
                      <div className="text-sm text-red-700">{job?.errorMessage}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default RenderingQueue;